import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { generateSchedule, GeneratedTask } from '@/lib/schedule';
import { getGerminationDays } from '@/lib/germinationDays';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay,
  isSameMonth, addMonths, subMonths, isToday, startOfWeek, endOfWeek,
  addDays
} from 'date-fns';
import {
  ChevronLeft, ChevronRight, Leaf, LogOut, Calendar, ListTodo,
  Sprout, Sun, Droplets, Scissors, Package, Settings, Crown, Layers, Eye
} from 'lucide-react';
import { motion } from 'framer-motion';
import { PaymentTestModeBanner } from '@/components/PaymentTestModeBanner';
import { UpgradeDialog } from '@/components/UpgradeDialog';
import { LegalFooter } from '@/components/LegalFooter';
import { SeasonBanner } from '@/components/SeasonBanner';
import { ArchetypeCard } from '@/components/ArchetypeCard';
import { TaskDetailDialog } from '@/components/TaskDetailDialog';
import { GerminationCheckDialog } from '@/components/GerminationCheckDialog';
import { WhatsNewBanner } from '@/components/WhatsNewBanner';
import gardenBgDashboard from '@/assets/garden-bg-dashboard.png';

const TASK_ICONS: Record<string, typeof Sprout> = {
  seed_sowing: Sprout, plant_out: Sun, harvest: Package,
  feed: Droplets, prune: Scissors, preserve: Package,
  support: Sun, harden_off: Sun, first_transplant: Sprout,
  second_transplant: Sprout, earth_up: Sun, thin_out: Scissors,
  pinch: Scissors, mulch: Sun, weed: Scissors, chit: Sprout,
  harvest_ongoing: Package, spring_feed: Droplets, autumn_cut_back: Scissors,
  germination_check: Eye,
};

const TASK_COLORS: Record<string, string> = {
  seed_sowing: 'bg-primary/10 text-primary border-primary/20',
  plant_out: 'bg-accent/10 text-accent-foreground border-accent/20',
  harvest: 'bg-garden-sun/10 text-foreground border-garden-sun/20',
  feed: 'bg-garden-sky/10 text-foreground border-garden-sky/20',
  preserve: 'bg-garden-bloom/10 text-foreground border-garden-bloom/20',
  germination_check: 'bg-garden-bloom/10 text-foreground border-garden-bloom/20',
  default: 'bg-muted/50 text-muted-foreground border-border/60',
};

interface Profile {
  growing_zone: string;
  weekday_minutes: number;
  weekday_count: number;
  weekend_hours_min: number;
  weekend_hours_max: number;
  setup_type: string;
  available_setups: string[] | null;
  onboarding_complete: boolean;
  display_name: string | null;
  gardener_archetype: string | null;
}

interface SavedTask {
  id: string;
  task_type: string;
  title: string;
  description: string | null;
  scheduled_date: string;
  estimated_minutes: number | null;
  completed: boolean | null;
  completed_at: string | null;
  user_plant_id: string;
}

type TaskCycleReference = Pick<SavedTask, 'scheduled_date' | 'completed_at'>;

const getTaskReferenceTime = (task: TaskCycleReference) =>
  new Date(task.completed_at ?? task.scheduled_date).getTime();

export default function Dashboard() {
  const { user, signOut } = useAuth();
  const { isPro } = useSubscription();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [tasks, setTasks] = useState<SavedTask[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [generating, setGenerating] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedTask, setSelectedTask] = useState<SavedTask | null>(null);

  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      toast.success('Subscription activated! 🎉 You now have unlimited plants.');
      window.history.replaceState({}, '', '/');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user) return;
    loadProfile();
    loadTasks().then(() => backfillGerminationChecks());
  }, [user]);

  const loadProfile = async () => {
    const { data } = await supabase.from('profiles').select('*').eq('user_id', user!.id).single();
    if (data) {
      setProfile(data as unknown as Profile);
      if (!data.onboarding_complete) navigate('/onboarding');
    }
  };

  const loadTasks = async () => {
    const { data } = await supabase
      .from('garden_tasks')
      .select('*')
      .eq('user_id', user!.id)
      .order('scheduled_date');
    if (data) {
      const today = format(new Date(), 'yyyy-MM-dd');
      const overdue = data.filter(t => t.scheduled_date < today && !t.completed);
      if (overdue.length > 0) {
        const ids = overdue.map(t => t.id);
        await supabase.from('garden_tasks')
          .update({ scheduled_date: today })
          .in('id', ids);
        setTasks(data.map(t =>
          ids.includes(t.id) ? { ...t, scheduled_date: today } : t
        ));
      } else {
        setTasks(data);
      }
    }
  };

  const generateTasks = async () => {
    if (!user || !profile) return;
    setGenerating(true);
    try {
      const { data: userPlants } = await supabase
        .from('user_plants')
        .select('id, plant_id, planted_date, setup_type, status, plants(name, plant_type, lifecycle_stages)')
        .eq('user_id', user.id);

      if (!userPlants || userPlants.length === 0) {
        toast.error('No plants selected! Go to onboarding to add plants.');
        return;
      }

      const today = new Date().toISOString().split('T')[0];

      // Find existing future tasks to know which plants already have tasks
      // and how much time is already used per day
      const { data: existingFutureTasks } = await supabase
        .from('garden_tasks')
        .select('user_plant_id, scheduled_date, estimated_minutes')
        .eq('user_id', user.id)
        .gte('scheduled_date', today);

      const plantIdsWithTasks = new Set((existingFutureTasks || []).map(t => t.user_plant_id));

      // Only generate for plants that don't already have scheduled tasks
      const newPlants = userPlants.filter(up => !plantIdsWithTasks.has(up.id));

      if (newPlants.length === 0 && existingFutureTasks && existingFutureTasks.length > 0) {
        toast.info('Schedule is already up to date!');
        await loadTasks();
        return;
      }

      // Build a map of minutes already used per day from existing tasks
      const existingMinutesPerDay: Record<string, number> = {};
      for (const t of existingFutureTasks || []) {
        existingMinutesPerDay[t.scheduled_date] = 
          (existingMinutesPerDay[t.scheduled_date] || 0) + (t.estimated_minutes || 15);
      }

      const schedule = generateSchedule(
        newPlants as any,
        profile.growing_zone,
        {
          weekday_minutes: profile.weekday_minutes,
          weekday_count: profile.weekday_count,
          weekend_hours_min: profile.weekend_hours_min,
          weekend_hours_max: profile.weekend_hours_max,
        },
        profile.available_setups || [profile.setup_type || 'direct'],
        new Date().getFullYear(),
        existingMinutesPerDay
      );

      const futureTasks = schedule.filter(t => t.scheduled_date >= today);
      const batchSize = 50;
      for (let i = 0; i < futureTasks.length; i += batchSize) {
        const batch = futureTasks.slice(i, i + batchSize).map(t => ({
          ...t,
          user_id: user.id,
        }));
        const { error: insertError } = await supabase.from('garden_tasks').insert(batch);
        if (insertError) throw insertError;
      }

      await loadTasks();
      const msg = newPlants.length > 0
        ? `Added ${futureTasks.length} tasks for ${newPlants.length} new plant${newPlants.length > 1 ? 's' : ''}! 🌱`
        : `Generated ${futureTasks.length} garden tasks! 🌱`;
      toast.success(msg);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const toggleTask = async (taskId: string, completed: boolean) => {
    const task = tasks.find(t => t.id === taskId);
    const completedAt = !completed ? new Date().toISOString() : null;

    await supabase.from('garden_tasks').update({
      completed: !completed,
      completed_at: completedAt,
    }).eq('id', taskId);
    setTasks(prev => prev.map(t => t.id === taskId
      ? { ...t, completed: !completed, completed_at: completedAt }
      : t));

    // When completing a seed_sowing task, auto-create a germination check
    if (!completed && task?.task_type === 'seed_sowing') {
      await createGerminationCheck(task);
    }
  };

  const createGerminationCheck = async (sowTask: SavedTask) => {
    if (!user) return;

    const { data: existingChecks } = await supabase.from('garden_tasks')
      .select('id, scheduled_date, completed_at')
      .eq('user_plant_id', sowTask.user_plant_id)
      .eq('task_type', 'germination_check')
      .order('scheduled_date', { ascending: false });

    const sowReferenceTime = Date.now();
    const alreadyCovered = (existingChecks || []).some(check =>
      getTaskReferenceTime(check) >= sowReferenceTime
    );

    if (alreadyCovered) return;

    const plantName = sowTask.title.split(':')[0]?.trim() || 'Plant';
    const germDays = getGerminationDays(plantName);
    const checkDate = addDays(new Date(), germDays);

    await supabase.from('garden_tasks').insert({
      user_id: user.id,
      user_plant_id: sowTask.user_plant_id,
      task_type: 'germination_check',
      title: `${plantName}: Check Germination`,
      description: `Check if ${plantName} seeds have sprouted (expected ~${germDays} days). If not, you can retry or remove the plant.`,
      scheduled_date: format(checkDate, 'yyyy-MM-dd'),
      estimated_minutes: 5,
    });

    toast.info(`Germination check scheduled for ${format(checkDate, 'd MMM')} 🔍`);
    await loadTasks();
  };

  // Backfill: create germination checks for already-completed sowing tasks that don't have one
  const backfillGerminationChecks = useCallback(async () => {
    if (!user) return;

    const { data: completedSows } = await supabase.from('garden_tasks')
      .select('id, user_plant_id, title, scheduled_date, completed_at')
      .eq('user_id', user.id)
      .eq('task_type', 'seed_sowing')
      .eq('completed', true)
      .order('completed_at', { ascending: false });

    if (!completedSows || completedSows.length === 0) return;

    const { data: existingChecks } = await supabase.from('garden_tasks')
      .select('id, user_plant_id, scheduled_date, completed_at, completed')
      .eq('user_id', user.id)
      .eq('task_type', 'germination_check');

    const checksByPlant = new Map<string, typeof existingChecks>();
    for (const check of existingChecks || []) {
      const plantChecks = checksByPlant.get(check.user_plant_id) || [];
      plantChecks.push(check);
      checksByPlant.set(check.user_plant_id, plantChecks);
    }

    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    const toInsert: any[] = [];
    const stalePendingIds = new Set<string>();
    const seenPlants = new Set<string>();

    for (const sow of completedSows) {
      // Only create a check for the most recent completed sow per plant
      if (seenPlants.has(sow.user_plant_id)) continue;
      seenPlants.add(sow.user_plant_id);

      const sowReferenceTime = getTaskReferenceTime(sow);
      const plantChecks = (checksByPlant.get(sow.user_plant_id) || []).sort(
        (a, b) => getTaskReferenceTime(b) - getTaskReferenceTime(a)
      );

      const pendingChecks = plantChecks.filter(check => !check.completed);
      const latestCompletedCheck = plantChecks.find(check => check.completed);

      if (latestCompletedCheck && getTaskReferenceTime(latestCompletedCheck) >= sowReferenceTime) {
        pendingChecks.forEach(check => stalePendingIds.add(check.id));
        continue;
      }

      if (pendingChecks.length > 0) {
        const [latestPending, ...duplicatePending] = pendingChecks;

        if (getTaskReferenceTime(latestPending) >= sowReferenceTime) {
          duplicatePending.forEach(check => stalePendingIds.add(check.id));
          continue;
        }

        pendingChecks.forEach(check => stalePendingIds.add(check.id));
      }

      const plantName = sow.title.split(':')[0]?.trim() || 'Plant';
      const germDays = getGerminationDays(plantName);
      
      // Schedule from completion date, or today if that's already passed
      const completedDate = sow.completed_at ? new Date(sow.completed_at) : new Date(sow.scheduled_date);
      const idealCheck = addDays(completedDate, germDays);
      const checkDateStr = idealCheck < today ? todayStr : format(idealCheck, 'yyyy-MM-dd');

      toInsert.push({
        user_id: user.id,
        user_plant_id: sow.user_plant_id,
        task_type: 'germination_check',
        title: `${plantName}: Check Germination`,
        description: `Check if ${plantName} seeds have sprouted (expected ~${germDays} days). If not, you can retry or remove the plant.`,
        scheduled_date: checkDateStr,
        estimated_minutes: 5,
      });
    }

    if (stalePendingIds.size > 0) {
      await supabase.from('garden_tasks').delete().in('id', [...stalePendingIds]);
    }

    if (toInsert.length > 0) {
      await supabase.from('garden_tasks').insert(toInsert);
      toast.info(`${toInsert.length} germination check${toInsert.length > 1 ? 's' : ''} added for your existing plants 🔍`);
    }

    if (stalePendingIds.size > 0 || toInsert.length > 0) {
      await loadTasks();
    }
  }, [user]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const tasksByDate = useMemo(() => {
    const map = new Map<string, SavedTask[]>();
    for (const task of tasks) {
      const key = task.scheduled_date;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(task);
    }
    return map;
  }, [tasks]);

  const sortTasksByCompletion = (tasks: SavedTask[]) =>
    [...tasks].sort((a, b) => (a.completed ? 1 : 0) - (b.completed ? 1 : 0));

  const selectedDayTasks = useMemo(() => {
    return sortTasksByCompletion(tasksByDate.get(format(selectedDate, 'yyyy-MM-dd')) || []);
  }, [selectedDate, tasksByDate]);

  const todayTasks = useMemo(() => {
    return sortTasksByCompletion(tasksByDate.get(format(new Date(), 'yyyy-MM-dd')) || []);
  }, [tasksByDate]);

  const completedToday = todayTasks.filter(t => t.completed).length;
  const progressPercent = todayTasks.length > 0 ? (completedToday / todayTasks.length) * 100 : 0;

  if (!profile) return (
    <div className="flex min-h-screen items-center justify-center bg-background botanical-dots">
      <Sprout className="h-8 w-8 animate-gentle-bounce text-primary" />
    </div>
  );

  return (
    <div className="min-h-screen bg-background relative">
      {/* Watercolour garden background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <img
          src={gardenBgDashboard}
          alt=""
          className="absolute bottom-0 left-0 w-full h-auto opacity-70 object-cover object-bottom"
          style={{ minHeight: '35%' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-background/40 to-background" />
      </div>
      <div className="relative z-[1]">
      <PaymentTestModeBanner />

      {/* Glass header */}
      <header className="border-b border-border/40 glass-header sticky top-0 z-10">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl garden-gradient shadow-soft">
              <Leaf className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">Garden VA</h1>
              <p className="text-xs text-muted-foreground">Hello, {profile.display_name || 'Gardener'} 👋</p>
            </div>
          </div>
          <div className="flex items-center gap-0.5 sm:gap-1.5">
            <Button variant="ghost" size="sm" onClick={() => navigate('/pricing')} className="rounded-xl text-xs hidden sm:inline-flex">
              Pricing
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/about')} className="rounded-xl text-xs hidden sm:inline-flex">
              About
            </Button>
            {!isPro && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUpgrade(true)}
                className="text-xs gap-1 rounded-xl border-border/60 shadow-soft hover:shadow-medium transition-all px-2 sm:px-3"
                aria-label="Upgrade"
              >
                <Crown className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Upgrade</span>
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => setView(v => v === 'calendar' ? 'list' : 'calendar')} className="rounded-xl" aria-label="Toggle view">
              {view === 'calendar' ? <ListTodo className="h-4 w-4" /> : <Calendar className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/progress')} className="rounded-xl" title="Plant Progress">
              <Layers className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} className="rounded-xl">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 space-y-6">
        {/* What's New banner */}
        <WhatsNewBanner />

        {/* Season banner */}
        <SeasonBanner />

        {/* Archetype card */}
        <ArchetypeCard archetype={profile.gardener_archetype} />

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="grid grid-cols-3 gap-3"
        >
          <div className="card-organic p-4 text-center">
            <div className="text-3xl font-bold text-primary">{todayTasks.length}</div>
            <div className="text-xs text-muted-foreground mt-1">Today's tasks</div>
          </div>
          <div className="card-organic p-4 text-center relative overflow-hidden">
            {/* Progress ring background */}
            <div className="text-3xl font-bold text-primary">{completedToday}<span className="text-lg text-muted-foreground font-normal">/{todayTasks.length}</span></div>
            <div className="text-xs text-muted-foreground mt-1">Completed</div>
            {todayTasks.length > 0 && (
              <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
                  className="h-full rounded-full garden-gradient"
                />
              </div>
            )}
          </div>
          <div className="card-organic p-4 text-center">
            <div className="text-3xl font-bold text-primary">{tasks.length}</div>
            <div className="text-xs text-muted-foreground mt-1">Total tasks</div>
          </div>
        </motion.div>

        {/* Empty state */}
        {tasks.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="card-organic border-dashed border-2 border-primary/20">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-3xl bg-primary/10">
                  <Sprout className="h-10 w-10 text-primary animate-gentle-bounce" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Ready to grow?</h2>
                <p className="text-muted-foreground mb-6 max-w-sm">
                  Generate your personalised garden schedule based on your plants, time, and setup.
                </p>
                <Button onClick={generateTasks} disabled={generating} className="garden-gradient shadow-medium rounded-xl h-11 px-6 text-base font-semibold transition-all hover:shadow-lifted hover:scale-[1.02] active:scale-[0.98]">
                  {generating ? 'Generating...' : 'Generate My Schedule 🌱'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {tasks.length > 0 && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={generateTasks} disabled={generating} className="rounded-xl border-border/60 shadow-soft hover:shadow-medium transition-all">
              {generating ? 'Regenerating...' : 'Regenerate Schedule'}
            </Button>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1fr_350px]">
          {/* Calendar */}
          {view === 'calendar' && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
            >
              <Card className="card-organic">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(m => subMonths(m, 1))} className="rounded-xl">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <CardTitle className="text-lg">{format(currentMonth, 'MMMM yyyy')}</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(m => addMonths(m, 1))} className="rounded-xl">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-0.5">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                      <div key={d} className="p-2 text-center text-xs font-medium text-muted-foreground">{d}</div>
                    ))}
                    {calendarDays.map(day => {
                      const dateKey = format(day, 'yyyy-MM-dd');
                      const dayTasks = tasksByDate.get(dateKey) || [];
                      const hasUncompleted = dayTasks.some(t => !t.completed);
                      const allComplete = dayTasks.length > 0 && !hasUncompleted;
                      const isSelected = isSameDay(day, selectedDate);

                      return (
                        <button
                          key={dateKey}
                          onClick={() => setSelectedDate(day)}
                          className={`relative min-h-[56px] rounded-xl p-1.5 text-left transition-all duration-200 hover:bg-muted/60 ${
                            !isSameMonth(day, currentMonth) ? 'opacity-25' : ''
                          } ${isSelected ? 'ring-2 ring-primary/60 bg-primary/5 shadow-soft' : ''} ${
                            isToday(day) && !isSelected ? 'bg-garden-cream' : ''
                          }`}
                        >
                          <span className={`text-xs font-medium ${isToday(day) ? 'text-primary font-bold' : 'text-foreground'}`}>
                            {format(day, 'd')}
                          </span>
                          {dayTasks.length > 0 && (
                            <div className="mt-0.5 flex flex-wrap gap-0.5">
                              {dayTasks.slice(0, 3).map((t, i) => (
                                <div
                                  key={i}
                                  className={`h-1.5 w-1.5 rounded-full transition-colors ${
                                    t.completed ? 'bg-primary/20' : 'bg-primary'
                                  }`}
                                />
                              ))}
                              {dayTasks.length > 3 && (
                                <span className="text-[8px] text-muted-foreground leading-none">+{dayTasks.length - 3}</span>
                              )}
                            </div>
                          )}
                          {allComplete && (
                            <div className="absolute right-1 top-1 text-primary text-[10px]">✓</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* List view */}
          {view === 'list' && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle className="text-lg">All Upcoming Tasks</CardTitle>
                </CardHeader>
                <CardContent className="max-h-[600px] overflow-y-auto space-y-2">
                  {tasks.filter(t => !t.completed).slice(0, 50).map((task, i) => (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <TaskItem task={task} onToggle={toggleTask} onTap={setSelectedTask} />
                    </motion.div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Day detail */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Card className="card-organic">
              <CardHeader>
                <CardTitle className="text-lg">
                  {isToday(selectedDate) ? "Today's Tasks" : format(selectedDate, 'EEEE, d MMMM')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDayTasks.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="text-3xl mb-2">🌿</div>
                    <p className="text-sm text-muted-foreground">No tasks scheduled for this day</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedDayTasks.map((task, i) => (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                      >
                        <TaskItem task={task} onToggle={toggleTask} onTap={setSelectedTask} />
                      </motion.div>
                    ))}
                    <div className="pt-3 text-xs text-muted-foreground text-center">
                      ⏱ ~{selectedDayTasks.reduce((sum, t) => sum + (t.estimated_minutes || 15), 0)} minutes total
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
      <UpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
      {selectedTask?.task_type === 'germination_check' ? (
        <GerminationCheckDialog
          task={selectedTask}
          open={!!selectedTask}
          onOpenChange={(open) => { if (!open) setSelectedTask(null); }}
          onTaskUpdated={loadTasks}
          userId={user!.id}
        />
      ) : (
        <TaskDetailDialog
          task={selectedTask}
          open={!!selectedTask}
          onOpenChange={(open) => { if (!open) setSelectedTask(null); }}
          onTaskUpdated={loadTasks}
        />
      )}
      <LegalFooter />
      </div>
    </div>
  );
}

function TaskItem({ task, onToggle, onTap }: { task: SavedTask; onToggle: (id: string, completed: boolean) => void; onTap: (task: SavedTask) => void }) {
  const Icon = TASK_ICONS[task.task_type] || Sprout;
  const colorClass = TASK_COLORS[task.task_type] || TASK_COLORS.default;

  const recurMatch = task.description?.match(/\((weekly|ongoing) until (.+?)\)$/);
  const cleanDescription = task.description?.replace(/\s*\((weekly|ongoing) until .+?\)$/, '') || '';
  const recurType = recurMatch?.[1];
  const recurUntil = recurMatch?.[2];

  return (
    <div
      className={`flex items-start gap-3 rounded-2xl border p-3.5 transition-all duration-200 cursor-pointer ${
        task.completed ? 'opacity-40 scale-[0.98]' : 'hover:shadow-soft'
      } ${colorClass}`}
      onClick={() => onTap(task)}
    >
      <Checkbox
        checked={task.completed || false}
        onCheckedChange={(e) => {
          if (task.task_type === 'germination_check') return; // force dialog flow
          onToggle(task.id, task.completed || false);
        }}
        onClick={(e) => e.stopPropagation()}
        className={`mt-0.5 rounded-md ${task.task_type === 'germination_check' ? 'opacity-50 cursor-not-allowed' : ''}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${task.completed ? 'line-through' : ''}`}>{task.title}</span>
          {recurUntil && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0 border-primary/20 text-primary rounded-full">
              {recurType === 'ongoing' ? '🌿' : '🔁'} until {recurUntil}
            </Badge>
          )}
        </div>
        {cleanDescription && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{cleanDescription}</div>
        )}
      </div>
      <div className="flex items-center gap-1.5 shrink-0 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-xs">{task.estimated_minutes || 15}m</span>
      </div>
    </div>
  );
}
